import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    const newPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${blockId}"] [ref="priceContainer"]`);
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice.cloneNode(true));
      // After replacing, check if we need to multiply by current quantity
      this.handleQuantityUpdate();
    }
  };

  /**
   * Handles quantity update from quantity-selector
   * @param {QuantitySelectorUpdateEvent} [event]
   */
  handleQuantityUpdate = (event) => {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer || !priceContainer.dataset.unitPrice) return;

    const unitPrice = parseFloat(priceContainer.dataset.unitPrice);

    // display_multiplier is the per-UOM quantity (e.g. 1000 for paper products).
    // This is what controls the label "/ 1000 SHEET" and the base display price.
    const displayMultiplier = parseInt(priceContainer.dataset.multiplier) || 1;

    // The main product price display should ALWAYS match its label (e.g., "/ 1000 SHEET").
    // Therefore, we calculate the price using the displayMultiplier (e.g., 1000)
    // and ignore the customer's selected quantity. The cart will handle actual subtotals.
    let totalPrice = Math.round(unitPrice * displayMultiplier);

    const priceEl = priceContainer.querySelector('.price-exc-gst');

    console.log('[ProductPrice] handleQuantityUpdate updated', {
      unitPrice,
      displayMultiplier,
      totalPrice,
      blockId: this.dataset.blockId
    });

    if (priceEl && window.theme?.Currency?.formatMoney) {
      priceEl.textContent = window.theme.Currency.formatMoney(totalPrice);
    }
  }
};

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
